import { useEffect, useCallback, useState, useRef } from "react";
import { X, Bookmark, BookmarkCheck } from "lucide-react";
import { renderArticleBody } from "@/components/articleBody";
import { AudioOverview } from "@/components/AudioOverview";
import { StoryReport } from "@/components/StoryReport";
import { CATEGORY_COLORS } from "@/components/StoryCard";

export const ArticleModal = ({ article, isOpen, onClose, isBookmarked = false, onBookmark = null }) => {
  const [imageError, setImageError] = useState(false);
  const modalRef = useRef(null);
  const closeBtnRef = useRef(null);
  const previousFocusRef = useRef(null);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") {
      onClose();
      return;
    }
    // Keep Tab inside the dialog — without a trap, keyboard focus escapes
    // into the (visually hidden) page behind the overlay.
    if (e.key === "Tab" && modalRef.current) {
      const focusables = modalRef.current.querySelectorAll(
        'button, [href], input, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
      // Move focus into the dialog; restore it to the opener on close.
      previousFocusRef.current = document.activeElement;
      closeBtnRef.current?.focus();
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
      if (previousFocusRef.current?.focus) previousFocusRef.current.focus();
    };
  }, [isOpen, handleKeyDown]);

  // Reset image error when article changes
  useEffect(() => {
    setImageError(false);
  }, [article?.id]);

  // Don't render anything when not open
  if (!isOpen) return null;
  if (!article) return null;

  // Category mapping wins over the stored color — older articles carry the
  // pre-spec (inverted) colors baked in at ingestion.
  const color = CATEGORY_COLORS[article.category] || article.color || "#3A86D4";

  const handleBackdropClick = (e) => {
    // Only close if clicking exactly on the backdrop, not any child elements
    if (e.target === e.currentTarget) {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/40 backdrop-blur-sm animate-fadeIn"
      onClick={handleBackdropClick}
      data-testid="article-modal-overlay"
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="article-modal-headline"
        className="bg-white rounded-lg max-w-[600px] w-full max-h-[88vh] overflow-y-auto relative animate-slideUp"
        data-testid="article-modal"
      >
        <div className="p-8 md:p-11 relative">
          {/* Action Buttons */}
          <div className="absolute top-4 right-4 flex items-center gap-2">
            {/* Solid frosted chips: these sit on top of the hero image, so they
                must carry their own contrast — a transparent gray button
                disappears on light skies and dark scenes alike. */}
            {onBookmark && (
              <button
                onClick={onBookmark}
                aria-label={isBookmarked ? "Remove from saved stories" : "Save this story"}
                className={`w-9 h-9 rounded-full flex items-center justify-center shadow-md backdrop-blur-sm transition-colors ${
                  isBookmarked
                    ? "bg-scoop-blue text-white"
                    : "bg-white/95 text-gray-600 hover:text-scoop-blue"
                }`}
                data-testid="modal-bookmark-btn"
              >
                {isBookmarked ? (
                  <BookmarkCheck className="w-4 h-4" />
                ) : (
                  <Bookmark className="w-4 h-4" />
                )}
              </button>
            )}
            <button
              ref={closeBtnRef}
              onClick={onClose}
              aria-label="Close story"
              className="w-9 h-9 rounded-full bg-white/95 shadow-md backdrop-blur-sm flex items-center justify-center text-gray-700 hover:bg-white hover:text-gray-900 transition-colors"
              data-testid="modal-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Hero Image */}
          {article.image_url && !imageError ? (
            <div className="rounded-lg overflow-hidden aspect-[16/9] mb-6 -mx-8 md:-mx-11">
              <img
                src={article.image_url}
                alt={article.headline || article.title}
                className="w-full h-full object-cover"
                onError={() => setImageError(true)}
              />
            </div>
          ) : (
            <div
              className="rounded-lg aspect-[16/9] mb-6 -mx-8 md:-mx-11 flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, #EEF5FF 0%, ${color}18 100%)` }}
            >
              <span className="text-6xl opacity-70" aria-hidden="true">{article.emoji || "📰"}</span>
            </div>
          )}

          {/* Category — same dot + label idiom as the story cards */}
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
            <span
              className="text-[0.67rem] font-bold tracking-widest uppercase"
              style={{ color }}
            >
              {article.sub_category || article.category}
            </span>
          </div>

          {/* Headline */}
          <h2
            id="article-modal-headline"
            className="font-display font-black text-[1.6rem] md:text-[1.85rem] leading-[1.15] tracking-tight text-scoop-ink mb-4 pr-16"
            data-testid="modal-headline"
          >
            {article.headline || article.title}
          </h2>

          {/* Deck */}
          <p className="text-[0.95rem] text-scoop-muted leading-relaxed italic mb-5 pb-5 border-b border-scoop-border">
            {article.deck || article.description}
          </p>

          {/* Full narration — the funnel's "or hear the whole story" promise
              must be true HERE, since the preview routes into this modal */}
          <div className="mb-6">
            <AudioOverview article={article} mode="full" />
          </div>

          {/* Body */}
          <div className="font-body-serif text-[1.02rem] text-gray-700 leading-[1.72] tracking-[-0.005em]">
            {renderArticleBody(article.body)}
          </div>

          <StoryReport article={article} />
        </div>
      </div>
    </div>
  );
};

export default ArticleModal;
